import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy, EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/** Recipe Filer sends the email for you. `send(to, message)` overrides the
 * default POST /share/email (used by the shopping list). */
export const EmailShareDialog = ({ open, onOpenChange, title, url, summary, kind = "link", heading, send, prefillSelf = false }) => {
  const { user } = useAuth();
  const loc = useLocation();
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  useEffect(() => {
    if (open && prefillSelf && user?.email) setTo((t) => t || user.email);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      if (send) await send(to.trim(), message);
      else await api.post("/share/email", { to: to.trim(), message, url, title, summary, kind });
      setState("sent");
      toast.success(`Email sent to ${to.trim()}`);
      setTimeout(() => { setState("idle"); setTo(""); setMessage(""); onOpenChange(false); }, 1400);
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Couldn't send email");
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  const next = encodeURIComponent(loc.pathname + loc.search);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-md" data-testid="email-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-rf-ink">{heading || "Send by email"}</DialogTitle>
          <DialogDescription>Recipe Filer sends it for you — no mail app needed.{title ? <> Sharing <strong>{title}</strong>.</> : null}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2">
          <input
            type="email" required value={to} onChange={(e) => setTo(e.target.value)} disabled={!user}
            placeholder="Their email address" aria-label="Recipient email"
            className="input-rf w-full disabled:opacity-60" data-testid="email-share-to"
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3} disabled={!user}
            placeholder="Add a message (optional)" aria-label="Optional message"
            className="input-rf w-full resize-none disabled:opacity-60" data-testid="email-share-message"
          />
          {user ? (
            <button
              type="submit" disabled={state !== "idle"}
              className={`btn-orange w-full justify-center inline-flex items-center gap-2 ${state === "sent" ? "!bg-emerald-600 !text-white" : ""}`}
              data-testid="email-share-send"
            >
              {state === "sent" ? <><Check size={16} weight="bold" /> Sent</> : state === "sending" ? "Sending…" : <><EnvelopeSimple size={16} /> Send email</>}
            </button>
          ) : (
            <div className="rounded-xl bg-rf-amber/15 p-3 text-sm text-rf-ink" data-testid="email-share-signin">
              <p className="mb-2">Sign in (free) so Recipe Filer can send this for you.</p>
              <div className="flex flex-wrap gap-2">
                <Link to={`/login?next=${next}`} className="btn-orange !py-2 !px-4 text-sm" data-testid="email-share-signin-link">Sign in to send</Link>
                <Link to={`/register?next=${next}`} className="btn-outline-ink !py-2 !px-4 text-sm" data-testid="email-share-register-link">Create account</Link>
              </div>
            </div>
          )}
        </form>
        {url && (
          <button onClick={copy} className="text-xs font-bold text-rf-orange inline-flex items-center gap-1 hover:underline" data-testid="email-share-copy-link">
            <Copy size={12} /> Or copy the link instead
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
